// "Guide me through a bereavement experience" — the manager-facing walkthrough
// of what actually happens when someone in their team is bereaved, and what
// they will need to do at each stage (PO 2026-08-06).
//
// WHAT THIS IS NOT. It is not the product tour (`ManagerWelcome`, which
// explains what Maeve does), and it is not the per-touch-point reasoning
// (`TOUCHPOINT_RATIONALE`, which explains why a specific date exists once a
// support pathway is already running). It is the thing a manager needs BEFORE
// either: an orientation to a situation most people manage once or twice in a
// career, read cold, with no pathway open and nothing configured. The PO's
// framing was "what they will need to do — return to work etc — this is just
// advice", and "just advice" is the operative constraint: nothing here asks
// the manager to action anything or reflects any employee's real state.
//
// DELIBERATELY STATIC, NOT MAEVE-GENERATED — same reasoning as
// `TOUCHPOINT_RATIONALE`, which see. A general walkthrough of a general
// experience is fixed knowledge, not an inference over anyone's circumstances;
// generating it per view would burn a Vertex call and an audit row to
// re-derive the same paragraphs, and could drift. Critically, Maeve has NO
// case context on this surface — a generated version would either be identical
// to this text or would be inventing specifics about a person it knows nothing
// about. Nothing here is attributed to Maeve; presenting authored product copy
// as Maeve speech would breach the §1.4 #8 anti-anthropomorphism rule.
//
// NOT YET CGC-SIGNED-OFF. This is clinical framing shown to a manager and
// carries the same gate as the rest of the framework's content. It is drafted
// from DESIGN §6.3's stage purposes, docs/13's stage table and §16's "not a
// performance conversation or a welfare assessment" constraint, the
// already-shipped employee-facing `SUPPORT_MOMENTS` copy, and the
// `TOUCHPOINT_RATIONALE` set it deliberately echoes so a manager doesn't meet
// two different accounts of the same framework. See PROJECT.md's "Remaining
// work — non-engineering".
//
// `avoid` is the one field with no precedent elsewhere in the codebase and is
// the most sensitive: it is the "what not to say" list every bereavement
// guidance document carries, and it is where a well-meaning manager does the
// most damage. It stays behavioural ("don't ask them to make decisions about
// their return in the first week") and never diagnostic — a manager is not
// being taught to assess grief, which is §5's platform-not-a-support-service
// line and the boundary the whole product is built on.

/** Stage identifiers. Stable across copy edits — they key the UI's anchors. */
export const BEREAVEMENT_GUIDE_STAGES = [
  'first_hours',
  'first_days',
  'the_funeral',
  'while_away',
  'planning_return',
  'first_weeks_back',
  'longer_term',
] as const;
export type BereavementGuideStage = (typeof BEREAVEMENT_GUIDE_STAGES)[number];

export interface BereavementGuideEntry {
  /** Short stage name. */
  title: string;
  /** Roughly when this stage falls, in words. Never a promise of a schedule. */
  when: string;
  /** What is happening for the bereaved person. Orientation, not assessment. */
  what: string;
  /** What the manager needs to do. The PO's "what they will need to do". */
  you: readonly string[];
  /** What tends to go wrong here. Behavioural only — never diagnostic. */
  avoid?: string;
}

export const BEREAVEMENT_GUIDE: Record<BereavementGuideStage, BereavementGuideEntry> = {
  first_hours: {
    title: 'The first hours',
    when: 'As soon as you hear',
    what: 'Almost nothing has been arranged yet and very little has sunk in. What people remember from this period is not what was said but whether anyone said anything at all.',
    you: [
      'Send a short message acknowledging the death. Short is not cold — it is easier to receive than a long one.',
      'Say who is covering their work, so they are not lying awake worrying about it.',
      'Tell them you will handle letting the team know, and ask what they want said.',
      'Give them a single point of contact — you or HR — so they are not repeating themselves.',
    ],
    avoid:
      'Waiting until you know the right words. A brief, plain message today is worth more than a considered one next week.',
  },
  first_days: {
    title: 'The first days',
    when: 'The first week',
    what: 'This period is intensely practical — registering the death, notifying people, arranging a funeral — often alongside travel and family logistics. Work is a background noise they need switched off, not managed.',
    you: [
      "Confirm what leave they are entitled to, in writing, from your organisation's policy — not from memory.",
      'If the policy is unclear or their situation does not fit it, ask HR rather than improvising an answer.',
      'Hand over their work properly. "Don\'t worry about anything" only reassures if someone is visibly holding it.',
      'Agree how and how often they want to hear from you, and then follow it.',
    ],
    avoid:
      'Asking when they expect to be back. It is the question they cannot answer yet, and being asked it makes leave feel conditional.',
  },
  the_funeral: {
    title: 'The funeral',
    when: 'Usually one to three weeks after the death, sometimes much later',
    what: 'A funeral can be weeks away, particularly where a death is unexpected or referred to a coroner. The wait itself is hard, and the day after a funeral is often harder than the day of it.',
    you: [
      'Ask whether colleagues would be welcome, and take the answer at face value either way.',
      'If the team wants to mark it, coordinate one gesture rather than letting several arrive separately.',
      'Make sure any leave covers travel and the days around it, not only the day itself.',
      'Check in a few days afterwards. This is when everyone else stops asking.',
    ],
  },
  while_away: {
    title: 'While they are away',
    when: 'Through their leave',
    what: 'Contact from work can be a lifeline or an intrusion, and which one it is varies by person and by week. The risk at this stage is not too much contact — it is silence that reads as being forgotten.',
    you: [
      'Keep to the contact pattern you agreed, even when there is no news. Consistency is the point.',
      'Keep messages free of anything that needs a decision or a reply.',
      'Pass on team news they would want to know, so returning does not mean walking into a changed room.',
      "Point them at what's available — your EAP, your organisation's listed support, their GP — without pressing them to use it.",
    ],
    avoid:
      'Routing work questions through them "just quickly". Once it happens once, they stop trusting that they are actually off.',
  },
  planning_return: {
    title: 'Planning the return',
    when: 'A week or two before they come back',
    what: 'Most people are anxious about the first day back long before it arrives — specifically about crying at work, about being watched, and about being asked how they are by everyone in turn.',
    you: [
      'Talk before the day, not on it. A short call removes most of the dread.',
      'Agree the practical shape: hours, workload, what is off their plate, and for how long.',
      'Agree what the team will be told and by whom, and what they would rather nobody mentioned.',
      'Agree an exit — a signal or a place they can go — if the day gets away from them.',
      'Write down what you agreed and share it, so it survives a busy fortnight.',
    ],
    avoid:
      'Treating the return date as fixed once set. Plans made in grief often need moving, and moving one is not a setback.',
  },
  first_weeks_back: {
    title: 'The first weeks back',
    when: 'The first month back at work',
    what: 'Concentration, memory and tolerance for pressure are commonly affected for months, and it is normal for this to worsen before it improves. Meanwhile everyone else has moved on and assumes the matter is closed.',
    you: [
      'Hold the reduced workload you agreed for as long as you agreed it, rather than quietly letting it refill.',
      'Keep short one-to-ones going. Predictable and brief beats occasional and searching.',
      'Judge the work, not the grief. If performance is genuinely a problem, that is an HR conversation, not one you improvise.',
      'Notice what the rest of the team is carrying — cover has a cost, and it lands on people who will not mention it.',
    ],
    avoid:
      'Reading a good week as the end of it. Grief is not linear, and treating an upswing as recovery makes the next hard week feel like failure.',
  },
  longer_term: {
    title: 'The longer term',
    when: 'Months one to twelve, and the anniversary',
    what: 'Difficult days rarely land where anyone would predict them. Birthdays, the anniversary of the death, and dates that mean nothing to anyone else are often harder than the funeral was.',
    you: [
      'Keep checking in past the point where it feels necessary. Six months in, almost nobody else is.',
      'Be aware that some dates will be hard without needing to know which ones or why.',
      'Expect the anniversary to be difficult, and expect the weeks before it to be difficult too.',
      'Be flexible about leave around dates that matter, without requiring an explanation.',
    ],
    avoid:
      'Treating the twelve-month mark as a finish line. It is a milestone in the framework, not in the person.',
  },
};

/**
 * Signals that always warrant escalation rather than management. Kept OUT of
 * the stage entries deliberately: it applies at every stage, and a manager
 * skimming for the stage they are in must not be able to miss it by reading
 * only one section.
 *
 * The boundary is §5's: besideU is a platform, not a support service, so this
 * says who to tell — never how to assess or intervene.
 */
export const BEREAVEMENT_GUIDE_ESCALATION = {
  heading: 'When to stop managing and escalate',
  body: 'You are not expected to assess how someone is coping, and you should not try to. If you are worried about their safety, if they tell you something that frightens you, or if you are simply out of your depth, that is the point to involve someone else — not a sign you have handled it badly.',
  actions: [
    "Contact your organisation's HR safety contact the same day. Do not sit on it overnight.",
    'If you believe someone is in immediate danger, call 999.',
    'Samaritans is free on 116 123, day or night. Shout offers text support on 85258.',
    'Their GP and your EAP, if you have one, are the routes to ongoing support — besideU is not a counselling service.',
  ],
} as const;
